import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("loading"); // loading | success | error
  const [msg, setMsg] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setState("error");
      setMsg("Missing verification token. Please open the link from your email again.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setVerifiedEmail(data?.email || "");
        // If the buyer is already signed in on this browser, refresh their session
        // so the gate flips immediately. If not, we just show a "sign in" CTA.
        if (user) {
          try { await refresh(); } catch { /* non-critical */ }
        }
        setState("success");
      } catch (err) {
        setState("error");
        setMsg(formatApiErrorDetail(err.response?.data?.detail) || "We couldn't verify that link. Please request a new one.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bone px-6" data-testid="verify-email-page">
      <div className="max-w-md w-full text-center">
        {state === "loading" && (
          <>
            <Loader2 size={40} strokeWidth={1.25} className="mx-auto mb-6 text-charcoal animate-spin"/>
            <h1 className="font-serif text-4xl tracking-tight mb-3">Verifying…</h1>
            <p className="text-sm text-stone">Confirming your email address.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="w-14 h-14 mx-auto mb-6 bg-[#EDF7EE] rounded-full flex items-center justify-center">
              <Check size={26} strokeWidth={2.5} className="text-[#2F7A31]"/>
            </div>
            <h1 className="font-serif text-4xl tracking-tight mb-3">Email verified.</h1>
            <p className="text-sm text-stone mb-8">
              {verifiedEmail ? <><span className="font-mono">{verifiedEmail}</span> is now verified. </> : "You're all set. "}
              Welcome to Bulk Buy Kits.
            </p>
            <button
              onClick={() => nav(user ? "/shop" : "/enter", { replace: true })}
              className="px-8 py-3 bg-charcoal text-bone tracking-[0.22em] text-xs uppercase hover:bg-oxblood transition-colors rounded-full"
              data-testid="verify-continue-btn"
            >
              {user ? "Continue to catalog" : "Sign in to continue"}
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="w-14 h-14 mx-auto mb-6 bg-oxblood/10 rounded-full flex items-center justify-center">
              <XCircle size={26} strokeWidth={2} className="text-oxblood"/>
            </div>
            <h1 className="font-serif text-4xl tracking-tight mb-3">Verification failed.</h1>
            <p className="text-sm text-stone mb-8" data-testid="verify-error-msg">{msg}</p>
            <button
              onClick={() => nav(user ? "/shop" : "/enter", { replace: true })}
              className="px-8 py-3 bg-charcoal text-bone tracking-[0.22em] text-xs uppercase hover:bg-oxblood transition-colors rounded-full"
              data-testid="verify-error-btn"
            >
              {user ? "Back to shop" : "Sign in — resend link from there"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
